import React, { useState, useEffect } from "react"
import {
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Button,
  Row,
  Col,
  Typography,
  Space,
  Divider,
  Alert,
  Spin,
  Tag,
  Tooltip,
  Card,
  Switch,
} from "antd"
import {
  EditOutlined,
  CopyOutlined,
  SaveOutlined,
  UserOutlined,
  PhoneOutlined,
  HomeOutlined,
  EnvironmentOutlined,
  NumberOutlined,
  FileTextOutlined,
  BuildOutlined,
  ToolOutlined,
} from "@ant-design/icons"
import Locations from "components/Common/Locations"
import { getCurrentUser } from "helpers/service_helper"

const { Text, Title } = Typography
const { TextArea } = Input

const ProjectUpdate = ({ rowData, mainProps }) => {
  const [form] = Form.useForm()
  const [open, setOpen] = useState(false)
  const [dataAddress, setDataAddress] = useState({
    sectionId: 0,
    fullAddress: {
      pro: "",
      cit: "",
      sec: "",
      lat: 35.311308,
      lng: 46.991271,
      mainAddress: "",
    },
  })

  useEffect(() => {
    if (open) {
      form.setFieldsValue({
        landlordName: rowData.landlordName,
        ownershipTypeEnum: rowData.ownershipTypeEnum,
        companyName: rowData.companyName,
        landlordNaCode: rowData.landlordNaCode,
        landlordPhoneNumber: rowData.landlordPhoneNumber,
        postalCode: rowData.postalCode,
        area: rowData.area,
        address: rowData.address,
        des1: rowData.des1,
        floorNumber: rowData.floorNumber,
        buildingTypeEnum: rowData.buildingTypeEnum,
        packageSerialNumber: rowData.packageSerialNumber,
        unitNumber: rowData.unitNumber,
        pipingTypeEnum: rowData.pipingTypeEnum || 0,
        hasBurial: rowData.hasBurial ?? false,
      })
    }
  }, [open, rowData, form])

  useEffect(() => {
    if (
      +dataAddress.sectionId !== 0 &&
      +dataAddress.sectionId !== rowData.idSection
    ) {
      form.setFieldValue("address", "")
    }
  }, [dataAddress.sectionId, rowData.idSection, form])

  const handleSubmit = async values => {
    const { onUpdateGasProject, onResetGasProjectFlag } = mainProps
    const mapToSave = {
      ...values,
      id: rowData.id,
      buildingTypeEnum: +values.buildingTypeEnum,
      IdSection: +dataAddress.sectionId || rowData.idSection,
      pipingTypeEnum: +values.pipingTypeEnum,
      hasBurial: !!values.hasBurial,
    }
    await onUpdateGasProject(mapToSave)
    // Reset error and success messages after a delay
    setTimeout(() => {
      if (onResetGasProjectFlag) {
        onResetGasProjectFlag()
      }
    }, 3000)
  }

  const copyFileNumber = () => {
    navigator.clipboard.writeText(rowData.fileNumber)
  }

  const ownershipTypeEnum = Form.useWatch("ownershipTypeEnum", form)

  const canEdit =
    rowData.projectLevelEnum === 0 ||
    getCurrentUser().role === "Administrator"

  return (
    <>
      <Space size={4}>
        <Button
          type="link"
          size="small"
          onClick={() => setOpen(true)}
          style={{ padding: 0, fontWeight: 600 }}
        >
          {rowData.fileNumber}
        </Button>
        <Tooltip title="کپی شماره پرونده">
          <Button
            type="text"
            size="small"
            icon={<CopyOutlined />}
            onClick={copyFileNumber}
          />
        </Tooltip>
      </Space>

      <Modal
        title={
          <Space>
            <EditOutlined />
            <span>ویرایش پرونده: {rowData.fileNumber}</span>
          </Space>
        }
        open={open}
        onCancel={() => setOpen(false)}
        width={800}
        footer={null}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          size="middle"
        >
          {/* Info Tags */}
          <Card
            size="small"
            style={{ marginBottom: 16, background: "#fafafa" }}
            styles={{ body: { padding: 12 } }}
          >
            <Space wrap>
              <Tag color="blue">
                نوع لوله کشی: {rowData.pipingType}
              </Tag>
              <Tag color="green">
                مالکیت: {mainProps.t(rowData.ownershipType)}
              </Tag>
              <Tag color="orange">
                تاریخ ثبت: {mainProps.t(rowData.solarRegisterDate)}
              </Tag>
            </Space>
          </Card>

          {/* Owner Information */}
          <Divider orientation="right" orientationMargin={0}>
            <Space>
              <UserOutlined />
              <Text strong>اطلاعات مالک</Text>
            </Space>
          </Divider>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="landlordName"
                label="نام و نام خانوادگی مالک"
                rules={[{ required: true, message: "این فیلد اجباری است" }]}
              >
                <Input
                  prefix={<UserOutlined style={{ color: "#bfbfbf" }} />}
                  placeholder="نام مالک"
                />
              </Form.Item>
            </Col>

            {ownershipTypeEnum === 2 && (
              <Col xs={24} sm={12}>
                <Form.Item
                  name="companyName"
                  label="نام شرکت"
                  rules={[{ required: true, message: "این فیلد اجباری است" }]}
                >
                  <Input placeholder="نام شرکت" />
                </Form.Item>
              </Col>
            )}

            <Col xs={24} sm={12}>
              <Form.Item
                name="landlordNaCode"
                label="کدملی مالک/شرکت"
                rules={[
                  { required: true, message: "این فیلد اجباری است" },
                  { len: 10, message: "کدملی باید ۱۰ رقم باشد" },
                ]}
              >
                <Input
                  prefix={<NumberOutlined style={{ color: "#bfbfbf" }} />}
                  placeholder="کدملی"
                  maxLength={10}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12}>
              <Form.Item
                name="landlordPhoneNumber"
                label="شماره تماس"
                rules={[
                  { required: true, message: "این فیلد اجباری است" },
                  { len: 11, message: "شماره تماس باید ۱۱ رقم باشد" },
                ]}
              >
                <Input
                  prefix={<PhoneOutlined style={{ color: "#bfbfbf" }} />}
                  placeholder="09123456789"
                  maxLength={11}
                  style={{ direction: "ltr" }}
                />
              </Form.Item>
            </Col>
          </Row>

          {/* Property Information */}
          <Divider orientation="right" orientationMargin={0}>
            <Space>
              <HomeOutlined />
              <Text strong>اطلاعات ملک</Text>
            </Space>
          </Divider>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="postalCode"
                label="کد پستی"
                rules={[{ required: true, message: "این فیلد اجباری است" }]}
              >
                <Input
                  prefix={<NumberOutlined style={{ color: "#bfbfbf" }} />}
                  placeholder="کد پستی"
                  maxLength={10}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12}>
              <Form.Item
                name="area"
                label="زیربنای حرارتی (مترمربع)"
                rules={[{ required: true, message: "این فیلد اجباری است" }]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  placeholder="متراژ"
                  min={0}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12}>
              <Form.Item
                name="floorNumber"
                label="طبقه"
                rules={[{ required: true, message: "این فیلد اجباری است" }]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  placeholder="شماره طبقه"
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12}>
              <Form.Item name="unitNumber" label="واحد">
                <Input placeholder="شماره واحد" />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12}>
              <Form.Item
                name="buildingTypeEnum"
                label="نوع کاربری"
                rules={[{ required: true, message: "این فیلد اجباری است" }]}
              >
                <Select placeholder="انتخاب نوع کاربری">
                  <Select.Option value={1}>مسکونی</Select.Option>
                  <Select.Option value={2}>تجاری</Select.Option>
                  <Select.Option value={3}>اداری</Select.Option>
                  <Select.Option value={4}>صنعتی</Select.Option>
                  <Select.Option value={5}>عمومی</Select.Option>
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} sm={12}>
              <Form.Item name="pipingTypeEnum" label="نوع لوله گذاری">
                <Select placeholder="انتخاب نوع لوله گذاری">
                  <Select.Option value={0}>روکار</Select.Option>
                  <Select.Option value={1}>توکار</Select.Option>
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} sm={12}>
              <Form.Item
                name="hasBurial"
                label="دفنی"
                valuePropName="checked"
              >
                <Switch
                  checkedChildren="دارد"
                  unCheckedChildren="ندارد"
                />
              </Form.Item>
            </Col>
          </Row>

          {/* Location */}
          <Divider orientation="right" orientationMargin={0}>
            <Space>
              <EnvironmentOutlined />
              <Text strong>موقعیت مکانی</Text>
            </Space>
          </Divider>

          <Row gutter={16}>
            <Col xs={24}>
              <Form.Item label="استان/شهر/بخش">
                <Locations
                  setDataAddress={setDataAddress}
                  matches_sm={true}
                  idSection={rowData.idSection}
                />
              </Form.Item>
            </Col>

            <Col xs={24}>
              <Form.Item
                name="address"
                label="آدرس"
                rules={[{ required: true, message: "این فیلد اجباری است" }]}
              >
                <TextArea
                  rows={2}
                  placeholder="آدرس کامل"
                  showCount
                  maxLength={500}
                />
              </Form.Item>
            </Col>

            <Col xs={24}>
              <Form.Item name="des1" label="توضیحات">
                <TextArea
                  rows={2}
                  placeholder="توضیحات اضافی"
                  showCount
                  maxLength={500}
                />
              </Form.Item>
            </Col>
          </Row>

          {/* Package Information */}
          <Divider orientation="right" orientationMargin={0}>
            <Space>
              <ToolOutlined />
              <Text strong>اطلاعات پکیج</Text>
            </Space>
          </Divider>

          <Card
            size="small"
            style={{ marginBottom: 16, background: "#f6ffed", border: "1px solid #b7eb8f" }}
            styles={{ body: { padding: 12 } }}
          >
            <Space wrap>
              <Text>
                <strong>سریال:</strong> {rowData.packageSerialNumber}
              </Text>
              <Divider type="vertical" />
              <Text>
                <strong>شرکت:</strong> {rowData.packageCompanyName}
              </Text>
              <Divider type="vertical" />
              <Text>
                <strong>نصاب:</strong> {rowData.packageInstallerName}
              </Text>
              <Divider type="vertical" />
              <Text>
                <strong>موبایل:</strong> {rowData.packageInstallerPhoneNumber}
              </Text>
            </Space>
          </Card>

          <Row gutter={16}>
            <Col xs={24}>
              <Form.Item name="packageSerialNumber" label="شماره سریال">
                <Input
                  prefix={<BuildOutlined style={{ color: "#bfbfbf" }} />}
                  placeholder="شماره سریال پکیج"
                />
              </Form.Item>
            </Col>
          </Row>

          {/* Footer */}
          <Divider />

          {canEdit ? (
            <Row justify="end">
              <Space>
                <Button onClick={() => setOpen(false)}>انصراف</Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SaveOutlined />}
                  loading={mainProps.loading}
                >
                  ذخیره تغییرات
                </Button>
              </Space>
            </Row>
          ) : (
            <Alert
              message="جهت نیاز به تغییر مشخصات به واحد گاز مراجعه نمایید"
              type="warning"
              showIcon
            />
          )}
        </Form>
      </Modal>
    </>
  )
}

export default ProjectUpdate
